import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import { loadMarkets, updatedText } from '../util';
import { GROUPS, UNIT, commoditiesOf, commodityPrice, commodityNaira, commodityWhen, changeView } from './Commodities';
import { ART, WHY, TINT } from './commodityArt';

/** "2026-10-20" -> "20 Oct" (daily) or "Oct 2026" (monthly) */
function shortDate(d, monthly) {
  const [y, mo, day] = String(d || '').split('-').map(Number);
  if (!y) return '';
  return new Date(Date.UTC(y, mo - 1, day || 1)).toLocaleDateString('en-GB', monthly ? {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  } : {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC'
  });
}

/** The price over the stored history, with its high and low marked */
function chart(c) {
  const h = (c.history || []).filter(p => Number(p[1]) > 0);
  if (h.length < 3) return null;
  const values = h.map(p => Number(p[1]));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const W = 300;
  const H = 90;
  const x = i => i / (h.length - 1) * W;
  const y = v => 6 + (1 - (v - min) / span) * (H - 12);
  const line = values.map((v, i) => x(i).toFixed(1) + ',' + y(v).toFixed(1)).join(' ');
  const area = '0,' + H + ' ' + line + ' ' + W + ',' + H;
  const monthly = c.interval === 'monthly';
  const up = values[values.length - 1] >= values[0];
  return m('figure.VouchaCmP-chart', [m('svg', {
    viewBox: `0 0 ${W} ${H}`,
    preserveAspectRatio: 'none',
    role: 'img',
    'aria-label': c.name + ' price, ' + shortDate(h[0][0], monthly) + ' to ' + shortDate(h[h.length - 1][0], monthly)
  }, [m('polygon', {
    points: area,
    class: 'VouchaCmP-area' + (up ? ' is-up' : ' is-down')
  }), m('polyline', {
    points: line,
    class: 'VouchaCmP-line' + (up ? ' is-up' : ' is-down')
  })]), m('figcaption.VouchaCmP-range', [m('span', shortDate(h[0][0], monthly)), m('span', ['Low ', m('b', commodityPrice(c, min)), ' · High ', m('b', commodityPrice(c, max))]), m('span', shortDate(h[h.length - 1][0], monthly))])]);
}

/**
 * /commodities: world prices of oil, gas, metals and farm produce, each with a drawing, the dollar price and
 * its naira equivalent, the change since the previous price, a chart of the last six months (daily prices) or
 * two years (monthly averages), and why it matters in Nigeria.
 */
export default class CommoditiesPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.markets = null;
    this.error = null;
    app.setTitle('Commodity prices today');
    loadMarkets().then(d => this.markets = d).catch(() => this.error = 'Could not load prices. Please try again shortly.').then(() => {
      m.redraw();
      // a link to one commodity (/commodities#gold) scrolls to it once the cards are drawn
      const id = window.location.hash.slice(1);
      if (id) setTimeout(() => {
        const el = document.getElementById('cm-' + id);
        if (el) el.scrollIntoView({
          block: 'start'
        });
      }, 50);
    });
  }
  view() {
    const list = commoditiesOf(this.markets);
    const updated = this.markets && this.markets.commodities && this.markets.commodities.updated_at;
    return m('.VouchaMarkets.VouchaCmP', [m('h4.App-titleControl.App-titleControl--text', 'Commodities'), m(Link, {
      className: 'VouchaMk-back',
      href: app.route('voucha.markets')
    }, '← Markets'), m('header.VouchaMk-head', [m('h1', 'Commodity prices today'), m('p.helpText', 'World prices of oil, gas, metals and farm produce in US dollars, with the naira equivalent at today’s rate. Oil, gas, gold and silver are daily prices; the rest are monthly world averages.'), this.markets ? m('.VouchaMarkets-updated', m('span', updatedText(updated))) : null]), this.error ? m('.VouchaMarkets-error', this.error) : null, !this.markets && !this.error ? m('.VouchaMarkets-loading', 'Loading prices…') : null, this.markets && !list.length ? m('section.VouchaMarkets-section', m('p.VouchaMarkets-empty', 'Commodity prices are not available yet.')) : null, GROUPS.map(([key, title]) => {
      const items = list.filter(c => c.group === key);
      if (!items.length) return null;
      return m('section.VouchaCmP-group', [m('h2', title), m('.VouchaCmP-grid', items.map(c => {
        const naira = commodityNaira(c, this.markets);
        return m('article.VouchaCmP-card', {
          key: c.key,
          id: 'cm-' + c.key
        }, [m('.VouchaCmP-top', [m('.VouchaCmP-art', {
          style: {
            background: TINT[c.group] || '#f2f2f2'
          }
        }, ART[c.key] || null), m('.VouchaCmP-title', [m('h3', c.name), m('span', (c.cents ? 'US cents per ' : 'US dollars per ') + (UNIT[c.unit] || c.unit))])]), m('.VouchaCmP-price', [m('b', commodityPrice(c)), changeView(c)]), m('.VouchaCmP-sub', [naira ? m('span', naira + ' at today’s rate') : null, m('span', commodityWhen(c))]), chart(c), WHY[c.key] ? m('p.VouchaCmP-why', WHY[c.key]) : null]);
      }))]);
    }), m('p.VouchaMk-source', ['Prices from Alpha Vantage; cocoa is the IMF’s monthly world price, via FRED. Naira equivalents use today’s dollar rate on ', m(Link, {
      href: '/markets'
    }, 'Markets'), '. Not investment advice.'])]);
  }
}
flarum.reg.add('voucha-markets', 'forum/components/CommoditiesPage', CommoditiesPage);